import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Brain,
  Key,
  Eye,
  EyeOff,
  Save,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Zap,
  Shield,
  Building2,
  Settings2,
  Sparkles,
  RefreshCw,
  ExternalLink,
  Info
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider logos/colors
const PROVIDER_STYLES = {
  anthropic: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', name: 'Anthropic Claude' },
  gemini: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', name: 'Google Gemini' },
  openai: { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-700', name: 'OpenAI GPT' },
};

export default function SystemAIConfiguration() {
  const { authToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Providers data
  const [providers, setProviders] = useState(null);
  
  // Per-provider API keys status
  const [providerKeys, setProviderKeys] = useState({});
  
  // System config state
  const [systemConfig, setSystemConfig] = useState({
    default_provider: 'gemini',
    default_model: 'gemini-2.5-flash',
    current_api_key: '',  // Key being entered for current provider
    allow_org_override: true
  });
  
  // UI state
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyChanged, setApiKeyChanged] = useState(false);

  // Fetch providers and system config
  const fetchData = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    
    try {
      // Fetch providers
      const providersRes = await fetch(`${API_URL}/api/llm/providers`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(data.providers);
      }
      
      // Fetch system config
      const configRes = await fetch(`${API_URL}/api/llm/system-config`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (configRes.ok) {
        const config = await configRes.json();
        setProviderKeys(config.provider_keys || {});
        setSystemConfig({
          default_provider: config.default_provider || 'gemini',
          default_model: config.default_model || 'gemini-2.5-flash',
          current_api_key: '',  // Never populated from server
          allow_org_override: config.allow_org_override !== false
        });
      }
    } catch (err) {
      console.error('Failed to fetch AI config:', err);
      toast.error('Failed to load AI configuration');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle provider change - reset the key input but show saved key status
  const handleProviderChange = (provider) => {
    setSystemConfig(prev => ({
      ...prev,
      default_provider: provider,
      default_model: providers?.[provider]?.default_model || '',
      current_api_key: ''  // Clear input when switching providers
    }));
    setApiKeyChanged(false);
    setTestResult(null);
  };

  // Handle model change
  const handleModelChange = (model) => {
    setSystemConfig(prev => ({ ...prev, default_model: model }));
    setTestResult(null);
  };

  // Handle API key change
  const handleApiKeyChange = (value) => {
    setSystemConfig(prev => ({ ...prev, current_api_key: value }));
    setApiKeyChanged(true);
    setTestResult(null);
  };

  // Get current provider's key status
  const getCurrentProviderKeyStatus = () => {
    return providerKeys[systemConfig.default_provider] || { configured: false, preview: null };
  };

  // Test configuration
  const testConfiguration = async () => {
    const keyToTest = apiKeyChanged ? systemConfig.current_api_key : null;
    const currentKeyStatus = getCurrentProviderKeyStatus();
    
    if (!keyToTest && !currentKeyStatus.configured) {
      toast.error(`Please enter an API key for ${PROVIDER_STYLES[systemConfig.default_provider]?.name || systemConfig.default_provider}`);
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/llm/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: systemConfig.default_provider,
          model: systemConfig.default_model,
          api_key: keyToTest || 'USE_EXISTING',  // Signal to use existing key for this provider
          prompt: "Say 'Configuration test successful!' and nothing else."
        })
      });
      
      const result = await response.json();
      setTestResult(result);
      
      if (result.success) {
        toast.success(`Test passed! Response: ${result.response}`);
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
    } catch (err) {
      console.error('Test failed:', err);
      toast.error('Failed to test configuration');
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  // Save configuration
  const saveConfiguration = async () => {
    setSaving(true);
    
    try {
      const payload = {
        default_provider: systemConfig.default_provider,
        default_model: systemConfig.default_model,
        allow_org_override: systemConfig.allow_org_override
      };
      
      // Include API key for the current provider if it was changed
      if (apiKeyChanged && systemConfig.current_api_key) {
        payload.provider_api_key = systemConfig.current_api_key;
      }
      
      const response = await fetch(`${API_URL}/api/llm/system-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('System AI configuration saved');
        setApiKeyChanged(false);
        fetchData();  // Refresh to get updated preview
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Get available models for selected provider
  const getAvailableModels = () => {
    if (!providers || !systemConfig.default_provider) return [];
    return providers[systemConfig.default_provider]?.models || [];
  };

  // Get provider style
  const getProviderStyle = (provider) => {
    return PROVIDER_STYLES[provider] || PROVIDER_STYLES.gemini;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const currentProviderStyle = getProviderStyle(systemConfig.default_provider);

  return (
    <div className="space-y-6">
      <PageHeader
        title="System AI Configuration"
        description="Configure system-wide AI defaults and BYOM (Bring Your Own Model) settings"
        icon={<Brain className="w-6 h-6 text-violet-500" />}
      />

      <Tabs defaultValue="system" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            System Defaults
          </TabsTrigger>
          <TabsTrigger value="hierarchy" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Key Hierarchy
          </TabsTrigger>
        </TabsList>

        {/* System Defaults Tab */}
        <TabsContent value="system" className="space-y-6">
          {/* Provider Selection */}
          <Card data-testid="provider-selection-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" />
                Default AI Provider
              </CardTitle>
              <CardDescription>
                Select the default LLM provider for system-wide AI features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                {providers && Object.entries(providers).map(([key, provider]) => {
                  const style = getProviderStyle(key);
                  const isSelected = systemConfig.default_provider === key;
                  
                  return (
                    <button
                      key={key}
                      data-testid={`provider-${key}`}
                      onClick={() => handleProviderChange(key)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? `${style.bg} ${style.border} ring-2 ring-offset-2 ring-violet-500` 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className={`text-lg font-semibold ${isSelected ? style.text : 'text-slate-700'}`}>
                        {provider.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {provider.models.length} models available
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-5 h-5 text-violet-500 mt-2 mx-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Model Selection */}
          <Card data-testid="model-selection-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-500" />
                Default Model
              </CardTitle>
              <CardDescription>
                Select the default model for {providers?.[systemConfig.default_provider]?.name || 'the provider'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getAvailableModels().map((model) => {
                  const isSelected = systemConfig.default_model === model.id;
                  
                  return (
                    <button
                      key={model.id}
                      data-testid={`model-${model.id}`}
                      onClick={() => handleModelChange(model.id)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        isSelected 
                          ? `${currentProviderStyle.bg} ${currentProviderStyle.border}` 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isSelected ? currentProviderStyle.text : 'text-slate-700'}`}>
                          {model.name}
                        </span>
                        {model.supports_thinking && (
                          <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                            Extended Thinking
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 font-mono">
                        {model.id}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* System API Key - Per Provider */}
          <Card data-testid="api-key-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-500" />
                {PROVIDER_STYLES[systemConfig.default_provider]?.name || 'Provider'} API Key
                {getCurrentProviderKeyStatus().configured && (
                  <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200">
                    Configured
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                API key for {PROVIDER_STYLES[systemConfig.default_provider]?.name || 'the selected provider'}.
                Each provider's key is stored separately.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="system-api-key">API Key for {PROVIDER_STYLES[systemConfig.default_provider]?.name}</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="system-api-key"
                      data-testid="system-api-key-input"
                      type={showApiKey ? 'text' : 'password'}
                      value={systemConfig.current_api_key}
                      onChange={(e) => handleApiKeyChange(e.target.value)}
                      placeholder={getCurrentProviderKeyStatus().configured 
                        ? `Current: ${getCurrentProviderKeyStatus().preview}` 
                        : `Enter ${PROVIDER_STYLES[systemConfig.default_provider]?.name || 'provider'} API key...`}
                      className="pr-10 font-mono text-sm"
                      autoComplete="off"
                      spellCheck="false"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={testConfiguration}
                    disabled={testing || (!apiKeyChanged && !getCurrentProviderKeyStatus().configured)}
                    data-testid="test-config-btn"
                  >
                    {testing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4" />
                    )}
                    <span className="ml-2">Test</span>
                  </Button>
                </div>
                
                {/* Show key length indicator when typing */}
                {systemConfig.current_api_key && (
                  <p className="text-xs text-slate-500">
                    Key length: {systemConfig.current_api_key.length} characters
                    {systemConfig.default_provider === 'anthropic' && systemConfig.current_api_key.startsWith('sk-ant-') && (
                      <span className="ml-2 text-emerald-600">✓ Valid Anthropic format</span>
                    )}
                    {systemConfig.default_provider === 'openai' && systemConfig.current_api_key.startsWith('sk-') && (
                      <span className="ml-2 text-emerald-600">✓ Valid OpenAI format</span>
                    )}
                    {systemConfig.default_provider === 'gemini' && systemConfig.current_api_key.startsWith('AIza') && (
                      <span className="ml-2 text-emerald-600">✓ Valid Google AI format</span>
                    )}
                  </p>
                )}
                
                {/* Show all configured providers */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg border">
                  <p className="text-sm font-medium text-slate-700 mb-2">Configured API Keys:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(providerKeys).map(([provider, status]) => (
                      <Badge 
                        key={provider}
                        variant="outline" 
                        className={status.configured 
                          ? `${PROVIDER_STYLES[provider]?.bg} ${PROVIDER_STYLES[provider]?.text} border-current` 
                          : 'bg-slate-100 text-slate-400'}
                      >
                        {PROVIDER_STYLES[provider]?.name || provider}: {status.configured ? `...${status.preview?.slice(-4) || '****'}` : 'Not set'}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Test Result */}
                {testResult && (
                  <div className={`p-3 rounded-lg mt-3 ${
                    testResult.success 
                      ? 'bg-emerald-50 border border-emerald-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {testResult.success ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className={testResult.success ? 'text-emerald-700' : 'text-red-700'}>
                        {testResult.success ? 'Test Passed' : 'Test Failed'}
                      </span>
                      {testResult.latency_ms && (
                        <Badge variant="outline" className="text-xs">
                          {testResult.latency_ms}ms
                        </Badge>
                      )}
                    </div>
                    {testResult.response && (
                      <p className="text-sm text-emerald-600 mt-1">{testResult.response}</p>
                    )}
                    {testResult.error && (
                      <p className="text-sm text-red-600 mt-1">{testResult.error}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Get API Key Links */}
              <div className="pt-4 border-t">
                <p className="text-sm text-slate-600 mb-2">Get an API key:</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://console.anthropic.com/settings/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                  >
                    Anthropic Console <ExternalLink className="w-3 h-3" />
                  </a>
                  <span className="text-slate-300">|</span>
                  <a
                    href="https://makersuite.google.com/app/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    Google AI Studio <ExternalLink className="w-3 h-3" />
                  </a>
                  <span className="text-slate-300">|</span>
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                  >
                    OpenAI Platform <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* BYOM Policy */}
          <Card data-testid="byom-policy-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-slate-600" />
                Organization BYOM Policy
              </CardTitle>
              <CardDescription>
                Control whether organizations can use their own API keys
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-700">Allow Organization Override</p>
                  <p className="text-sm text-slate-500">
                    When enabled, organizations can configure their own API keys for premium features
                  </p>
                </div>
                <Switch
                  data-testid="allow-org-override-switch"
                  checked={systemConfig.allow_org_override}
                  onCheckedChange={(checked) => 
                    setSystemConfig(prev => ({ ...prev, allow_org_override: checked }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={saveConfiguration}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="save-system-config-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Configuration
            </Button>
          </div>
        </TabsContent>

        {/* Key Hierarchy Tab */}
        <TabsContent value="hierarchy" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-violet-500" />
                API Key Resolution Hierarchy
              </CardTitle>
              <CardDescription>
                Understanding how API keys are resolved for each organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Visual Hierarchy */}
              <div className="space-y-4">
                {/* Level 1 */}
                <div className="flex items-center gap-4 p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-violet-900">Organization Key (BYOM)</h4>
                    <p className="text-sm text-violet-700">
                      If an organization has enabled BYOM and configured their own API key, it takes highest priority.
                    </p>
                  </div>
                  <Badge className="bg-violet-600">Highest Priority</Badge>
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  <div className="w-0.5 h-6 bg-slate-300" />
                </div>

                {/* Level 2 */}
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">System Fallback Key</h4>
                    <p className="text-sm text-amber-700">
                      If organization doesn't have BYOM, the system-wide key configured above is used.
                    </p>
                  </div>
                  <Badge className="bg-amber-600">Fallback</Badge>
                </div>

              </div>

              {/* Info Box */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Why BYOM?</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Bring Your Own Model allows power users to use premium AI models like Claude 4.6 Extended 
                    at their own API cost. This enables features like <strong>Extended Thinking</strong> for 
                    transparent Go/No-Go decisions with full reasoning trace visibility.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
